import { Space, Radio, Select, Typography, Alert, Divider, Spin } from 'antd';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { jt, t } from 'ttag';

import NotFoundContent from 'common/components/NotFoundContent';
import { SALES_EMAIL } from 'common/services/support/types';

import { LICENSE_TYPE_EVENT_CREDIT, LICENSE_TYPE_USER_CREDIT } from '@domains/Organization/service';

import { useDuplicateEventSearch } from './queries';

const SOURCE_BLANK = 'blank';
export const SOURCE_DUPLICATE = 'duplicate';

const HELP_URL =
  'https://help.eventmobi.com/en/knowledge/getting-started-how-do-i-activate-my-event-credit';

const Subtitle = styled(Typography.Text)`
  display: block;
  font-size: 0.85em;
`;

const SourceOption = styled.div`
  padding-left: 24px;
`;

const Content = ({
  currentMode,
  hasContract,
  source,
  sourceEvent,
  setSourceEvent,
  setSource,
  orgId,
  children = null,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Fetch as soon as the dropdown opens so recent events show before any typing.
  const { data: events = [], isFetching } = useDuplicateEventSearch(orgId, search, open);

  const debouncedSetSearch = useMemo(() => debounce(setSearch, 250), []);

  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const handleOpenChange = visible => {
    setOpen(visible);
    if (!visible) {
      debouncedSetSearch.cancel();
      setSearch('');
    }
  };

  const handleEventSelect = option => {
    setSourceEvent(events.find(event => event.id === option.value) || null);
  };

  const handleSourceChange = e => {
    const src = e.target?.value;
    setSource(src);
    if (src === SOURCE_BLANK && sourceEvent?.id) {
      setSourceEvent(null);
    }
  };

  if (currentMode === LICENSE_TYPE_USER_CREDIT && !hasContract) {
    const contactUs = (
      <a key="contact-us" href={`mailto:${SALES_EMAIL}`} target="_blank" rel="noreferrer noopener">
        {t`contact us`}
      </a>
    );
    return (
      <Space orientation="vertical" style={{ width: '100%' }}>
        <Typography.Title level={5}>{t`Content`}</Typography.Title>
        <Alert
          showIcon
          type="error"
          title={t`No Active Contract`}
          description={jt`Your organization is under User Credit License, but there is no active contract. Please ${contactUs} to resolve this.`}
        />
      </Space>
    );
  }

  const learnLink = (
    <Typography.Link key="learn" href={HELP_URL} target="_blank" rel="noreferrer noopener">
      {t`Learn what will be duplicated.`}
    </Typography.Link>
  );

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Radio.Group onChange={handleSourceChange} value={source} style={{ width: '100%' }}>
        <Space orientation="vertical" size={0} style={{ width: '100%' }}>
          <Radio value={SOURCE_BLANK}>
            {t`Blank`}
            <Subtitle type="secondary">{t`Start with a new empty event.`}</Subtitle>
          </Radio>
          <Divider style={{ margin: '12px 0' }} />
          <Radio value={SOURCE_DUPLICATE}>
            {t`Duplicate Previous Event`}
            <Subtitle type="secondary">
              {jt`Start with a previous event design and library set up. ${learnLink}`}
            </Subtitle>
          </Radio>
        </Space>
      </Radio.Group>
      {source === SOURCE_DUPLICATE && (
        <SourceOption>
          <Select
            style={{ width: '100%' }}
            value={
              sourceEvent && {
                value: sourceEvent.id,
                label: `${sourceEvent.name} (${sourceEvent.code})`,
              }
            }
            showSearch={{ onSearch: debouncedSetSearch, filterOption: false }}
            open={open}
            loading={isFetching}
            placeholder={t`Previous Event Name`}
            options={events.map(event => ({
              value: event.id,
              label: `${event.name} (${event.code})`,
            }))}
            labelInValue
            onSelect={handleEventSelect}
            notFoundContent={
              isFetching ? (
                <div style={{ textAlign: 'center', margin: '1em' }}>
                  <Spin size="small" />
                </div>
              ) : (
                <NotFoundContent />
              )
            }
            onOpenChange={handleOpenChange}
          />
        </SourceOption>
      )}
      {children}
    </Space>
  );
};

Content.propTypes = {
  currentMode: PropTypes.oneOf([LICENSE_TYPE_EVENT_CREDIT, LICENSE_TYPE_USER_CREDIT]).isRequired,
  hasContract: PropTypes.bool.isRequired,
  source: PropTypes.string,
  sourceEvent: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
  setSourceEvent: PropTypes.func,
  setSource: PropTypes.func,
  orgId: PropTypes.string.isRequired,
  children: PropTypes.node,
};

export default Content;
